/**
 * External Tasks chips - Task Name, Placement ID, Candidate ID, Candidate Name, Status.
 */
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { ChipsContainer } from 'app/ComponentLibrary/Filter';

const STATUS_LABELS: Record<string, string> = {
  '1': 'New',
  '2': 'Pending',
  '3': 'In Progress',
  '4': 'Completed',
  '5': 'Cancelled',
  '6': 'On Hold',
};

export interface DashboardExternalTasksChipsProps {
  onApplyFilter: (values: Record<string, unknown>) => void;
}

export const DashboardExternalTasksChips: React.FC<DashboardExternalTasksChipsProps> = ({ onApplyFilter }) => {
  const { getValues, setValue, watch } = useFormContext();

  const search = watch('search');
  const placementId = watch('placementId');
  const candidateId = watch('candidateId');
  const candidateName = watch('candidateName');
  const statusIds = watch('statusIds');
  const dateRange = watch('dateRange');

  const chips = React.useMemo(() => {
    const result: Record<string, { label: string; enableDelete: boolean; value: unknown }[]> = {};
    if (search && String(search).trim()) {
      result.search = [{ label: `Task Name : ${search}`, enableDelete: true, value: search }];
    }
    if (placementId && String(placementId).trim()) {
      result.placementId = [{ label: `Placement ID : ${placementId}`, enableDelete: true, value: placementId }];
    }
    if (candidateId && String(candidateId).trim()) {
      result.candidateId = [{ label: `Candidate ID : ${candidateId}`, enableDelete: true, value: candidateId }];
    }
    if (candidateName && String(candidateName).trim()) {
      result.candidateName = [{ label: `Candidate Name : ${candidateName}`, enableDelete: true, value: candidateName }];
    }
    if (statusIds && typeof statusIds === 'object' && 'value' in statusIds) {
      const v = (statusIds as { value: string }).value;
      if (v) {
        result.statusIds = [
          {
            label: `Status : ${STATUS_LABELS[v] ?? (statusIds as { name?: string }).name ?? v}`,
            enableDelete: true,
            value: statusIds,
          },
        ];
      }
    }
    return result;
  }, [search, placementId, candidateId, candidateName, statusIds]);

  const handleChipDelete = (field: string) => {
    const values = getValues();
    if (field === 'search') {
      setValue('search', '');
      onApplyFilter({ ...values, search: '' });
    } else if (field === 'placementId') {
      setValue('placementId', '');
      onApplyFilter({ ...values, placementId: '' });
    } else if (field === 'candidateId') {
      setValue('candidateId', '');
      onApplyFilter({ ...values, candidateId: '' });
    } else if (field === 'candidateName') {
      setValue('candidateName', '');
      onApplyFilter({ ...values, candidateName: '' });
    } else if (field === 'statusIds') {
      setValue('statusIds', null);
      onApplyFilter({ ...values, statusIds: null });
    }
  };

  if (Object.keys(chips).length === 0) return null;

  return (
    <ChipsContainer chips={chips} onClick={() => {}} onDelete={handleChipDelete} />
  );
};
